import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Paper, Typography, Button, CircularProgress, Card, CardContent,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, useTheme, Autocomplete, TextField,
} from '@mui/material';
import {
  Refresh as RefreshIcon, Inventory, FileDownload as ExcelIcon,
  PictureAsPdf as PdfIcon, Print as PrintIcon, TrendingUp, TrendingDown, CheckCircle, ErrorOutline,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useLanguage } from '../../contexts/LanguageContext';
import { useFiscalDateRange } from '../../hooks/useFiscalDateRange';
import { companyInfoService, CompanyInfo } from '../../services/companyInfo.service';
import { warehousesService } from '../../services/warehouses.service';
import { itemsService } from '../../services/items.service';
import { Warehouse } from '../../types/warehouses.types';
import { Item } from '../../types/items.types';
import { itemLifecycleService, ItemLifecycleReportResultDto, ItemLifecycleSectionDto } from '../../services/itemLifecycle.service';
import { toast } from 'react-toastify';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { fmtUnitPrice } from '../../utils/formatNumber';

const ItemLifecycleReport: React.FC = () => {
  const { language } = useLanguage();
  const isRTL = language === 'ar';
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const [result, setResult] = useState<ItemLifecycleReportResultDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [companyInfo, setCompanyInfo] = useState<CompanyInfo | null>(null);
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const { minDate, maxDate } = useFiscalDateRange();
  const [itemId, setItemId] = useState<number | ''>('');
  const [whId, setWhId] = useState<number | '' | 'all'>('all');
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);

  useEffect(() => {
    companyInfoService.getCompanyInfo().then(setCompanyInfo).catch(() => {});
    warehousesService.getAll().then(setWarehouses).catch(() => {});
    itemsService.getAll().then(setItems).catch(() => {});
  }, []);

  const loadReport = async () => {
    if (!itemId) {
      toast.warn(isRTL ? 'اختر الصنف' : 'Select item');
      return;
    }
    setLoading(true);
    try {
      const fmtDate = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
      const data = await itemLifecycleService.getReport(
        Number(itemId),
        whId === 'all' ? 0 : Number(whId), // 0 = all warehouses
        fromDate ? fmtDate(fromDate) : undefined,
        toDate ? fmtDate(toDate) : undefined,
      );
      setResult(data);
      toast.success(isRTL ? 'تم التحميل' : 'Loaded');
    } catch { toast.error(isRTL ? 'فشل التحميل' : 'Failed'); }
    finally { setLoading(false); }
  };

  const fmt = (n: number) => (n || 0).toFixed(3);
  const reportTitle = isRTL ? 'قصة حياة الصنف' : 'Item Lifecycle Story';
  const compName = isRTL ? companyInfo?.companyNameAr : companyInfo?.companyName;

  const sections = useMemo(() => {
    if (!result) return [] as { sec: ItemLifecycleSectionDto; color: string; icon: string; direction: 'in' | 'out' | 'mixed' }[];
    return [
      { sec: result.opening, color: '#1565C0', icon: '📦', direction: 'in' as const },
      { sec: result.purchases, color: '#2E7D32', icon: '🛒', direction: 'in' as const },
      { sec: result.transfersIn, color: '#0288D1', icon: '⬇️', direction: 'in' as const },
      { sec: result.issues, color: '#E65100', icon: '🚚', direction: 'out' as const },
      { sec: result.transfersOut, color: '#C62828', icon: '⬆️', direction: 'out' as const },
      { sec: result.internalIssues, color: '#6A1B9A', icon: '🔧', direction: 'out' as const },
      { sec: result.stockCounts, color: '#00838F', icon: '📊', direction: 'mixed' as const },
    ].filter(s => s.sec.lines.length > 0);
  }, [result]);

  const exportToExcel = async () => {
    if (!result) return;
    try {
      const wb = new ExcelJS.Workbook();
      const ws = wb.addWorksheet(reportTitle);
      if (companyInfo?.logoBase64) {
        try {
          const img = wb.addImage({ base64: companyInfo.logoBase64.split(',').pop() || companyInfo.logoBase64, extension: 'png' });
          ws.addImage(img, { tl: { col: 0, row: 0 }, ext: { width: 100, height: 80 } });
        } catch {}
      }
      ws.mergeCells('C1:H1');
      ws.getCell('C1').value = compName || '';
      ws.getCell('C1').font = { size: 18, bold: true, color: { argb: 'FF1565C0' } };
      ws.getCell('C1').alignment = { horizontal: 'center' };

      ws.mergeCells('A3:H3');
      const tc = ws.getCell('A3');
      tc.value = `${reportTitle} — ${isRTL ? result.itemNameAr || result.itemName : result.itemName} (${result.itemCode})`;
      tc.font = { size: 14, bold: true, color: { argb: 'FFFFFFFF' } };
      tc.alignment = { horizontal: 'center' };
      tc.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1565C0' } };

      ws.mergeCells('A4:H4');
      ws.getCell('A4').value = `${isRTL ? 'المخزن' : 'Warehouse'}: ${isRTL ? result.warehouseNameAr || result.warehouseName : result.warehouseName}   |   ${isRTL ? 'من' : 'From'}: ${result.fromDate ? new Date(result.fromDate).toLocaleDateString('en-GB') : '-'}   ${isRTL ? 'إلى' : 'To'}: ${result.toDate ? new Date(result.toDate).toLocaleDateString('en-GB') : '-'}`;
      ws.getCell('A4').alignment = { horizontal: 'center' };
      ws.getCell('A4').font = { italic: true, size: 11 };

      let row = 6;
      const headers = [isRTL ? 'التاريخ' : 'Date', isRTL ? 'رقم السند' : 'Document', isRTL ? 'الحالة' : 'Status', isRTL ? 'الطرف' : 'Counterparty', isRTL ? 'الكمية' : 'Qty', isRTL ? 'الوحدة' : 'Unit', isRTL ? 'سعر الوحدة' : 'Unit Cost', isRTL ? 'القيمة' : 'Value'];

      sections.forEach(({ sec, color }) => {
        ws.mergeCells(`A${row}:H${row}`);
        const bc = ws.getCell(`A${row}`);
        bc.value = `${isRTL ? sec.titleAr : sec.titleEn}  (${sec.lineCount})`;
        bc.font = { size: 12, bold: true, color: { argb: 'FFFFFFFF' } };
        bc.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF' + color.replace('#', '') } };
        bc.alignment = { horizontal: isRTL ? 'right' : 'left' };
        row++;

        const hr = ws.getRow(row);
        headers.forEach((v, i) => {
          const c = hr.getCell(i + 1);
          c.value = v;
          c.font = { bold: true, color: { argb: 'FFFFFFFF' } };
          c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF424242' } };
          c.alignment = { horizontal: 'center' };
        });
        row++;

        sec.lines.forEach(line => {
          const r = ws.getRow(row);
          r.getCell(1).value = new Date(line.date).toLocaleDateString('en-GB');
          r.getCell(2).value = line.documentNumber;
          r.getCell(3).value = line.status;
          r.getCell(4).value = isRTL ? line.counterpartyNameAr || line.counterpartyName : line.counterpartyName;
          r.getCell(5).value = line.quantity;
          r.getCell(6).value = isRTL ? line.unitNameAr || line.unitName : line.unitName;
          r.getCell(7).value = line.unitCost;
          r.getCell(8).value = line.totalValue;
          row++;
        });

        const tr = ws.getRow(row);
        tr.getCell(1).value = isRTL ? 'الإجمالي' : 'Total';
        tr.getCell(5).value = sec.totalQuantity;
        tr.getCell(8).value = sec.totalValue;
        tr.eachCell(c => { c.font = { bold: true }; c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE3F2FD' } }; });
        row += 2;
      });

      ws.mergeCells(`A${row}:H${row}`);
      ws.getCell(`A${row}`).value = `${isRTL ? 'إجمالي الداخل' : 'Total IN'}: ${fmt(result.totalInQuantity)}  |  ${isRTL ? 'إجمالي الخارج' : 'Total OUT'}: ${fmt(result.totalOutQuantity)}  |  ${isRTL ? 'المتوقع' : 'Expected Ending'}: ${fmt(result.expectedEndingQuantity)}  |  ${isRTL ? 'الحالي' : 'Current'}: ${fmt(result.currentStockLevelQuantity)}`;
      ws.getRow(row).eachCell(c => { c.font = { bold: true, size: 12, color: { argb: 'FFFFFFFF' } }; c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF1565C0' } }; });

      ws.columns = [{ width: 13 }, { width: 18 }, { width: 12 }, { width: 26 }, { width: 14 }, { width: 12 }, { width: 14 }, { width: 14 }];
      const buf = await wb.xlsx.writeBuffer();
      const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `ItemLifecycle_${result.itemCode}_${Date.now()}.xlsx`;
      a.click();
      toast.success(isRTL ? 'تم التصدير' : 'Exported');
    } catch { toast.error(isRTL ? 'فشل' : 'Failed'); }
  };

  const exportToPdf = async () => {
    if (!result) return;
    try {
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const pw = doc.internal.pageSize.width;
      if (companyInfo?.logoBase64) { try { doc.addImage(companyInfo.logoBase64, 'PNG', 12, 8, 30, 25); } catch {} }
      doc.setFont('helvetica', 'bold'); doc.setFontSize(18); doc.setTextColor(21, 101, 192);
      doc.text(compName || '', pw / 2, 15, { align: 'center' });
      doc.setFillColor(21, 101, 192); doc.roundedRect(pw / 2 - 80, 32, 160, 10, 3, 3, 'F');
      doc.setFontSize(12); doc.setTextColor(255, 255, 255);
      doc.text(`Item Lifecycle - ${result.itemName} (${result.itemCode}) @ ${result.warehouseName}`, pw / 2, 38.5, { align: 'center' });

      let startY = 50;
      sections.forEach(({ sec, color }) => {
        const rgb = [parseInt(color.slice(1, 3), 16), parseInt(color.slice(3, 5), 16), parseInt(color.slice(5, 7), 16)];
        autoTable(doc, {
          head: [
            [{ content: `${sec.titleEn} (${sec.lineCount})`, colSpan: 8, styles: { fillColor: rgb as any, textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 10 } }],
            ['Date', 'Document', 'Status', 'Counterparty', 'Qty', 'Unit', 'Unit Cost', 'Value']
          ],
          body: [
            ...sec.lines.map(l => [
              new Date(l.date).toLocaleDateString('en-GB'),
              l.documentNumber,
              l.status,
              l.counterpartyName,
              fmt(l.quantity),
              l.unitName,
              fmt(l.unitCost),
              fmt(l.totalValue),
            ]),
            [{ content: 'Total', colSpan: 4, styles: { fontStyle: 'bold', fillColor: [227, 242, 253] } }, fmt(sec.totalQuantity), '', '', fmt(sec.totalValue)]
          ],
          startY,
          theme: 'grid',
          headStyles: { fillColor: [66, 66, 66], textColor: [255, 255, 255], fontSize: 8, fontStyle: 'bold', halign: 'center' },
          bodyStyles: { fontSize: 7 },
        });
        startY = (doc as any).lastAutoTable.finalY + 4;
      });

      autoTable(doc, {
        body: [[{ content: `IN: ${fmt(result.totalInQuantity)}  /  OUT: ${fmt(result.totalOutQuantity)}  /  Expected: ${fmt(result.expectedEndingQuantity)}  /  Current: ${fmt(result.currentStockLevelQuantity)}  /  Mismatch: ${fmt(result.mismatch)}`, styles: { fillColor: [21, 101, 192], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 10, halign: 'center' } }]],
        startY,
        theme: 'plain',
      });

      doc.save(`ItemLifecycle_${result.itemCode}_${new Date().toISOString().split('T')[0]}.pdf`);
      toast.success(isRTL ? 'تم التصدير' : 'Exported');
    } catch { toast.error(isRTL ? 'فشل' : 'Failed'); }
  };

  const handlePrint = () => {
    if (!result) return;
    const w = window.open('', '_blank', 'width=1100,height=700');
    if (!w) return;
    const sectionsHtml = sections.map(({ sec, color }) => `
      <div style="margin-top:14px">
        <div style="background:${color};color:white;padding:6px 10px;font-weight:bold;font-size:12px">${isRTL ? sec.titleAr : sec.titleEn} (${sec.lineCount})</div>
        <table style="width:100%;border-collapse:collapse;font-size:10px;margin-top:4px">
          <thead><tr>
            <th>${isRTL ? 'التاريخ' : 'Date'}</th><th>${isRTL ? 'رقم السند' : 'Document'}</th>
            <th>${isRTL ? 'الحالة' : 'Status'}</th><th>${isRTL ? 'الطرف' : 'Counterparty'}</th>
            <th>${isRTL ? 'الكمية' : 'Qty'}</th><th>${isRTL ? 'الوحدة' : 'Unit'}</th>
            <th>${isRTL ? 'سعر الوحدة' : 'Unit Cost'}</th><th>${isRTL ? 'القيمة' : 'Value'}</th>
          </tr></thead>
          <tbody>
            ${sec.lines.map(l => `<tr>
              <td>${new Date(l.date).toLocaleDateString('en-GB')}</td>
              <td>${l.documentNumber}</td>
              <td>${l.status}</td>
              <td>${isRTL ? l.counterpartyNameAr || l.counterpartyName : l.counterpartyName}</td>
              <td><b>${fmt(l.quantity)}</b></td>
              <td>${isRTL ? l.unitNameAr || l.unitName : l.unitName}</td>
              <td>${fmt(l.unitCost)}</td>
              <td><b>${fmt(l.totalValue)}</b></td>
            </tr>`).join('')}
            <tr style="background:#E3F2FD;font-weight:bold">
              <td colspan="4">${isRTL ? 'الإجمالي' : 'Total'}</td>
              <td>${fmt(sec.totalQuantity)}</td><td></td><td></td><td>${fmt(sec.totalValue)}</td>
            </tr>
          </tbody>
        </table>
      </div>`).join('');

    w.document.write(`<html dir="${isRTL ? 'rtl' : 'ltr'}"><head><title>${reportTitle}</title><style>
      body{font-family:'Segoe UI','Tajawal',sans-serif;margin:20px;color:#333}
      .header{display:flex;align-items:center;justify-content:space-between;border-bottom:3px solid #1565C0;padding-bottom:10px;margin-bottom:10px}
      .header img{max-height:60px} .header h1{color:#1565C0;margin:0;font-size:20px}
      th{background:#424242;color:white;padding:6px;text-align:${isRTL ? 'right' : 'left'};border:1px solid #424242}
      td{padding:5px 6px;border:1px solid #ddd}
      .grand{margin-top:14px;background:#1565C0;color:white;padding:10px;text-align:center;font-size:13px;font-weight:bold}
      @media print{body{margin:10px}}
    </style></head><body>
    <div class="header">
      <div>${companyInfo?.logoBase64 ? `<img src="${companyInfo.logoBase64}"/>` : ''}</div>
      <div style="text-align:center;flex:1">
        <h1>${reportTitle}</h1>
        <div style="color:#666;font-size:11px">${compName || ''}</div>
        <div style="color:#1565C0;font-size:13px;font-weight:bold;margin-top:4px">${isRTL ? result.itemNameAr || result.itemName : result.itemName} <span style="color:#666">(${result.itemCode})</span></div>
        <div style="color:#2E7D32;font-size:12px;font-weight:bold">${isRTL ? 'المخزن' : 'Warehouse'}: ${isRTL ? result.warehouseNameAr || result.warehouseName : result.warehouseName}</div>
        <div style="color:#666;font-size:10px;margin-top:2px">${isRTL ? 'من' : 'From'}: ${result.fromDate ? new Date(result.fromDate).toLocaleDateString('en-GB') : '-'} - ${isRTL ? 'إلى' : 'To'}: ${result.toDate ? new Date(result.toDate).toLocaleDateString('en-GB') : '-'}</div>
      </div>
      <div style="width:80px"></div>
    </div>
    ${sectionsHtml}
    <div class="grand">
      ${isRTL ? 'إجمالي الداخل' : 'Total IN'}: ${fmt(result.totalInQuantity)}  |
      ${isRTL ? 'إجمالي الخارج' : 'Total OUT'}: ${fmt(result.totalOutQuantity)}  |
      ${isRTL ? 'المتوقع' : 'Expected'}: ${fmt(result.expectedEndingQuantity)}  |
      ${isRTL ? 'الحالي' : 'Current'}: ${fmt(result.currentStockLevelQuantity)}  |
      ${isRTL ? 'الفرق' : 'Mismatch'}: ${fmt(result.mismatch)}
    </div>
    <div style="text-align:center;margin-top:10px;color:#999;font-size:9px">${new Date().toLocaleString(isRTL ? 'ar-KW' : 'en-GB')}</div>
    </body></html>`);
    w.document.close();
    setTimeout(() => w.print(), 500);
  };

  return (
    <Box sx={{ p: 3, direction: isRTL ? 'rtl' : 'ltr' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{ background: 'linear-gradient(135deg, #1565C0 0%, #42A5F5 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            {reportTitle}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {isRTL ? 'قصة حياة صنف داخل مخزن معين خلال فترة محددة: الافتتاحي + المشتريات + الصرفيات + التحويلات' : 'Complete history of a single item in a warehouse: opening + purchases + issues + transfers'}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
          <Autocomplete size="small" sx={{ minWidth: 280 }}
            options={items}
            getOptionLabel={(o) => `${o.itemCode} — ${isRTL ? o.itemNameAr || o.itemName : o.itemName}`}
            isOptionEqualToValue={(o, v) => o.itemId === v.itemId}
            value={items.find(i => i.itemId === itemId) || null}
            onChange={(_, v) => setItemId(v?.itemId || '')}
            renderInput={params => <TextField {...params} label={isRTL ? 'الصنف' : 'Item'} required />} />
          <Autocomplete size="small" sx={{ minWidth: 240 }}
            options={[{ warehouseId: 0 as number, warehouseName: isRTL ? 'كل المخازن' : 'All Warehouses', warehouseNameAr: isRTL ? 'كل المخازن' : 'All Warehouses' } as any, ...warehouses]}
            getOptionLabel={(o: any) => isRTL ? o.warehouseNameAr || o.warehouseName : o.warehouseName}
            isOptionEqualToValue={(o: any, v: any) => o.warehouseId === v.warehouseId}
            value={whId === 'all' ? { warehouseId: 0, warehouseName: isRTL ? 'كل المخازن' : 'All Warehouses', warehouseNameAr: isRTL ? 'كل المخازن' : 'All Warehouses' } as any : (warehouses.find(w => w.warehouseId === whId) || null)}
            onChange={(_, v: any) => setWhId(!v || v.warehouseId === 0 ? 'all' : v.warehouseId)}
            renderInput={params => <TextField {...params} label={isRTL ? 'المخزن' : 'Warehouse'} required />} />
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker label={isRTL ? 'من تاريخ' : 'From'} value={fromDate} onChange={d => setFromDate(d)}
              format="dd/MM/yyyy" minDate={minDate} maxDate={toDate || maxDate}
              slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } } }} />
            <DatePicker label={isRTL ? 'إلى تاريخ' : 'To'} value={toDate} onChange={d => setToDate(d)}
              format="dd/MM/yyyy" minDate={fromDate || minDate} maxDate={maxDate}
              slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } } }} />
          </LocalizationProvider>
          <Button variant="contained" startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
            onClick={loadReport} disabled={loading}
            sx={{ background: 'linear-gradient(135deg, #1565C0 0%, #42A5F5 100%)', px: 3, borderRadius: 3 }}>
            {loading ? (isRTL ? 'جاري...' : 'Loading...') : (isRTL ? 'عرض' : 'Load')}
          </Button>
          {result && (<>
            <Button variant="contained" size="small" startIcon={<ExcelIcon />} onClick={exportToExcel} sx={{ background: 'linear-gradient(45deg, #4CAF50 30%, #66BB6A 90%)', color: 'white', borderRadius: 3 }}>Excel</Button>
            <Button variant="contained" size="small" startIcon={<PdfIcon />} onClick={exportToPdf} sx={{ background: 'linear-gradient(45deg, #f44336 30%, #e57373 90%)', color: 'white', borderRadius: 3 }}>PDF</Button>
            <Button variant="contained" size="small" startIcon={<PrintIcon />} onClick={handlePrint} sx={{ background: 'linear-gradient(45deg, #FF9800 30%, #FFB74D 90%)', color: 'white', borderRadius: 3 }}>{isRTL ? 'طباعة' : 'Print'}</Button>
          </>)}
        </Box>
      </Box>

      {result && (
        <>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(5, 1fr)' }, gap: 2, mb: 3 }}>
            <Card sx={{ background: 'linear-gradient(135deg, #1565C0 0%, #42A5F5 100%)', color: 'white', borderRadius: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Inventory /><Typography variant="body2" sx={{ opacity: 0.9 }}>{isRTL ? 'افتتاحي' : 'Opening'}</Typography></Box>
                <Typography variant="h5" fontWeight="bold">{fmt(result.openingQuantity)}</Typography>
              </CardContent>
            </Card>
            <Card sx={{ background: 'linear-gradient(135deg, #2E7D32 0%, #66BB6A 100%)', color: 'white', borderRadius: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><TrendingUp /><Typography variant="body2" sx={{ opacity: 0.9 }}>{isRTL ? 'إجمالي الداخل' : 'Total IN'}</Typography></Box>
                <Typography variant="h5" fontWeight="bold">{fmt(result.totalInQuantity)}</Typography>
              </CardContent>
            </Card>
            <Card sx={{ background: 'linear-gradient(135deg, #E65100 0%, #FFA726 100%)', color: 'white', borderRadius: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><TrendingDown /><Typography variant="body2" sx={{ opacity: 0.9 }}>{isRTL ? 'إجمالي الخارج' : 'Total OUT'}</Typography></Box>
                <Typography variant="h5" fontWeight="bold">{fmt(result.totalOutQuantity)}</Typography>
              </CardContent>
            </Card>
            <Card sx={{ background: 'linear-gradient(135deg, #5E35B1 0%, #9575CD 100%)', color: 'white', borderRadius: 3 }}>
              <CardContent>
                <Typography variant="body2" sx={{ opacity: 0.9 }}>{isRTL ? 'المتوقع' : 'Expected'}</Typography>
                <Typography variant="h5" fontWeight="bold">{fmt(result.expectedEndingQuantity)}</Typography>
              </CardContent>
            </Card>
            <Card sx={{ background: Math.abs(result.mismatch) < 0.001 ? 'linear-gradient(135deg, #1B5E20 0%, #43A047 100%)' : 'linear-gradient(135deg, #C62828 0%, #EF5350 100%)', color: 'white', borderRadius: 3 }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {Math.abs(result.mismatch) < 0.001 ? <CheckCircle /> : <ErrorOutline />}
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>{isRTL ? 'الحالي / فرق' : 'Current / Mismatch'}</Typography>
                </Box>
                <Typography variant="h6" fontWeight="bold">{fmt(result.currentStockLevelQuantity)}</Typography>
                <Typography variant="caption">{isRTL ? 'فرق' : 'Δ'} {fmt(result.mismatch)}</Typography>
              </CardContent>
            </Card>
          </Box>

          <Paper sx={{ p: 2, mb: 3, borderRadius: 3, background: isDark ? 'rgba(255,255,255,0.05)' : 'linear-gradient(135deg, #FAFAFA 0%, #F0F4F8 100%)' }}>
            <Typography variant="h6" fontWeight="bold" sx={{ color: '#1565C0' }}>
              {isRTL ? result.itemNameAr || result.itemName : result.itemName} <Typography component="span" sx={{ color: '#666' }}>({result.itemCode})</Typography>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {isRTL ? 'المخزن' : 'Warehouse'}: <b>{isRTL ? result.warehouseNameAr || result.warehouseName : result.warehouseName}</b>
              {' | '}
              {isRTL ? 'الوحدة الأساسية' : 'Primary Unit'}: <b>{isRTL ? result.primaryUnitNameAr || result.primaryUnitName : result.primaryUnitName}</b>
            </Typography>
          </Paper>

          {sections.length === 0 ? (
            <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
              <Typography color="text.secondary">{isRTL ? 'لا توجد حركات في هذه الفترة' : 'No movements in this period'}</Typography>
            </Paper>
          ) : (
            sections.map(({ sec, color, icon, direction }) => (
              <Paper key={sec.sectionKey} sx={{ mb: 3, borderRadius: 3, overflow: 'hidden' }}>
                <Box sx={{ background: `linear-gradient(90deg, ${color} 0%, ${color}CC 100%)`, color: 'white', p: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {icon} {isRTL ? sec.titleAr : sec.titleEn} <Chip label={sec.lineCount} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: 'white', ml: 1 }} />
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Chip size="small" label={`${isRTL ? 'الكمية' : 'Qty'}: ${fmt(sec.totalQuantity)}`} sx={{ bgcolor: 'white', color, fontWeight: 'bold' }} />
                    <Chip size="small" label={`${isRTL ? 'القيمة' : 'Value'}: ${fmt(sec.totalValue)}`} sx={{ bgcolor: 'white', color, fontWeight: 'bold' }} />
                  </Box>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ bgcolor: '#424242', color: 'white', fontWeight: 'bold' }}>{isRTL ? 'التاريخ' : 'Date'}</TableCell>
                        <TableCell sx={{ bgcolor: '#424242', color: 'white', fontWeight: 'bold' }}>{isRTL ? 'رقم السند' : 'Document'}</TableCell>
                        <TableCell sx={{ bgcolor: '#424242', color: 'white', fontWeight: 'bold' }}>{isRTL ? 'الحالة' : 'Status'}</TableCell>
                        {result.allWarehouses && (
                          <TableCell sx={{ bgcolor: '#424242', color: 'white', fontWeight: 'bold' }}>{isRTL ? 'المخزن' : 'Warehouse'}</TableCell>
                        )}
                        <TableCell sx={{ bgcolor: '#424242', color: 'white', fontWeight: 'bold' }}>{isRTL ? 'الطرف' : 'Counterparty'}</TableCell>
                        <TableCell align="right" sx={{ bgcolor: '#424242', color: 'white', fontWeight: 'bold' }}>{isRTL ? 'الكمية' : 'Qty'}</TableCell>
                        <TableCell sx={{ bgcolor: '#424242', color: 'white', fontWeight: 'bold' }}>{isRTL ? 'الوحدة' : 'Unit'}</TableCell>
                        <TableCell align="right" sx={{ bgcolor: '#424242', color: 'white', fontWeight: 'bold' }}>{isRTL ? 'سعر الوحدة' : 'Unit Cost'}</TableCell>
                        <TableCell align="right" sx={{ bgcolor: '#424242', color: 'white', fontWeight: 'bold' }}>{isRTL ? 'القيمة' : 'Value'}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sec.lines.map((line, idx) => (
                        <TableRow key={idx} sx={{ '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)' } }}>
                          <TableCell>{new Date(line.date).toLocaleDateString('en-GB')}</TableCell>
                          <TableCell sx={{ fontWeight: 600, color }}>{line.documentNumber}</TableCell>
                          <TableCell><Chip size="small" label={line.status} sx={{ bgcolor: line.status === 'Posted' ? '#E8F5E9' : '#FFF3E0', color: line.status === 'Posted' ? '#1B5E20' : '#E65100', fontWeight: 'bold' }} /></TableCell>
                          {result.allWarehouses && (
                            <TableCell sx={{ fontSize: 12, color: '#0288D1', fontWeight: 500 }}>{isRTL ? line.warehouseNameAr || line.warehouseName : line.warehouseName}</TableCell>
                          )}
                          <TableCell>{isRTL ? line.counterpartyNameAr || line.counterpartyName : line.counterpartyName}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold', color: direction === 'mixed' ? (line.quantity >= 0 ? '#2E7D32' : '#C62828') : (direction === 'in' ? '#2E7D32' : '#C62828') }}>
                            {direction === 'mixed' ? (line.quantity >= 0 ? '+' : '') : (direction === 'in' ? '+' : '-')}{fmt(line.quantity)}
                          </TableCell>
                          <TableCell>{isRTL ? line.unitNameAr || line.unitName : line.unitName}</TableCell>
                          <TableCell align="right">{fmtUnitPrice(line.unitCost)}</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 'bold' }}>{fmt(line.totalValue)}</TableCell>
                        </TableRow>
                      ))}
                      <TableRow sx={{ bgcolor: '#E3F2FD' }}>
                        <TableCell colSpan={result.allWarehouses ? 5 : 4} sx={{ fontWeight: 'bold', color: '#0D47A1' }}>{isRTL ? 'إجمالي القسم' : 'Section Total'}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', color }}>{fmt(sec.totalQuantity)}</TableCell>
                        <TableCell colSpan={2}></TableCell>
                        <TableCell align="right" sx={{ fontWeight: 'bold', color: '#0D47A1' }}>{fmt(sec.totalValue)}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            ))
          )}

          {sections.length > 0 && (
            <Paper sx={{ background: 'linear-gradient(135deg, #1565C0 0%, #42A5F5 100%)', color: 'white', p: 2, borderRadius: 3, textAlign: 'center' }}>
              <Typography variant="h6" fontWeight="bold">
                {isRTL ? `إجمالي الداخل ${fmt(result.totalInQuantity)} | إجمالي الخارج ${fmt(result.totalOutQuantity)} | تسوية الجرد ${result.stockCountNetVariance >= 0 ? '+' : ''}${fmt(result.stockCountNetVariance)} | المتوقع ${fmt(result.expectedEndingQuantity)} | الحالي ${fmt(result.currentStockLevelQuantity)} | الفرق ${fmt(result.mismatch)}` : `IN ${fmt(result.totalInQuantity)} | OUT ${fmt(result.totalOutQuantity)} | StockCount Adj ${result.stockCountNetVariance >= 0 ? '+' : ''}${fmt(result.stockCountNetVariance)} | Expected ${fmt(result.expectedEndingQuantity)} | Current ${fmt(result.currentStockLevelQuantity)} | Mismatch ${fmt(result.mismatch)}`}
              </Typography>
            </Paper>
          )}
        </>
      )}

      {!result && !loading && (
        <Paper sx={{ p: 8, textAlign: 'center', borderRadius: 3 }}>
          <Inventory sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">{isRTL ? 'اختر الصنف والمخزن والفترة ثم اضغط "عرض"' : 'Select item, warehouse, and date range, then click "Load"'}</Typography>
        </Paper>
      )}
    </Box>
  );
};

export default ItemLifecycleReport;
